import { useState, useEffect, useRef, MutableRefObject } from 'react';
import { Box, TextFieldProps, TextFieldVariants } from '@mui/material';
import { IValidationResult, useTextWidth } from '@diana/core';
import { ValidationTooltip } from '../ValidationTooltip/ValidationTooltip';
import { StyledTextField } from '../../FlowBuilder2Page.styles';

export function TextFieldWithValidationTooltip<Variant extends TextFieldVariants>(
  props: { variant?: Variant } & Omit<TextFieldProps, 'variant'> & {
      autoWidth?: boolean;
      validationResult?: IValidationResult | null;
      fontSize?: string;
    },
) {
  const {
    value,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onChange,
    autoWidth,
    validationResult,
    autoFocus,
    fontSize,
    inputRef: inputRefFromProps,
    multiline = false,
    InputProps = {},
    inputProps = {},
    sx = {},
    ...textFieldProps
  } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const currentInputRef = (inputRefFromProps ??
    inputRef) as MutableRefObject<HTMLInputElement | null>;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [width, setWidth] = useState<number>(0);

  const { getWidth } = useTextWidth();

  const getTextWidth = (text: string) => {
    return getWidth(text, {
      fontSize: fontSize ?? '14px',
      fontWeight: 500,
    });
  };

  useEffect(() => {
    if (width === 0 && autoWidth && !multiline) {
      const currentWidth = getTextWidth(value as string);
      setWidth(currentWidth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, autoWidth, multiline]);

  useEffect(() => {
    if (autoFocus && currentInputRef.current) {
      const el = currentInputRef.current;
      el.focus();

      const length = el.value.length;
      el.setSelectionRange(length, length);
    }
  }, [autoFocus, currentInputRef]);

  return (
    <Box sx={{ position: 'relative', alignSelf: 'flex-start', width: '100%', zIndex: 1000 }}>
      <ValidationTooltip
        validationResult={validationResult}
        open={!!validationResult && (isFocused || isHovered)}
      >
        <StyledTextField
          {...textFieldProps}
          multiline={multiline}
          inputRef={currentInputRef}
          width={autoWidth ? width + 12 : undefined}
          value={value}
          onChange={(e) => {
            const currentWidth = getTextWidth(e.target.value);
            setWidth(currentWidth);
            if (onChange) onChange(e);
          }}
          onFocus={(e) => {
            setIsFocused(true);
            if (onFocus) onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (onBlur) onBlur(e);
          }}
          onMouseEnter={(e) => {
            setIsHovered(true);
            if (onMouseEnter) onMouseEnter(e);
          }}
          onMouseLeave={(e) => {
            setIsHovered(false);
            if (onMouseLeave) onMouseLeave(e);
          }}
          InputProps={{ ...InputProps, sx: InputProps?.sx ?? {} }}
          inputProps={{ ...inputProps, sx: inputProps?.sx ?? {} }}
          sx={{
            ...(multiline && { position: 'absolute', top: 0, left: 0, zIndex: 1000 }),
            ...sx,
          }}
        />
      </ValidationTooltip>
    </Box>
  );
}
